import type { SxProps, Theme } from "@mui/material";
import { Box, IconButton, ListItem, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import type { Maybe } from "@entrilia/shared/types.ts";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper.ts";
import type { ReportField, SortField } from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, Ordering } from "../../../../../../../../api/types/svTypes.ts";
import SortAscIcon from "@entrilia/shared/icons/SortAscIcon";
import SortDescIcon from "@entrilia/shared/icons/SortDescIcon";
import { isAggregation } from "../../../../utilities/fieldsHelper.ts";
import FieldDimensionCaption from "../../../viewer/common/fields/field-captions/FieldDimensionCaption.tsx";

interface Props {
  sortField: SortField;
  reportField?: Maybe<ReportField>;
  sx?: SxProps<Theme>;
  onRemoveItem?: (field: SortField) => void;
  onChangeSorting?: (field: SortField, order: Ordering) => void;
  onEndMoveItem?: (field: SortField) => void;
  onDragMove?: (field: SortField, replaceField: SortField) => void;
  readonlyMode?: boolean;
}
const SortFieldItem = memo(
  ({ sortField, reportField, sx, onRemoveItem, onChangeSorting, onEndMoveItem, onDragMove, readonlyMode }: Props) => {
    const caption = useMemo(() => {
      if (reportField && isAggregation(reportField)) {
        return <FieldDimensionCaption field={reportField} />;
      }

      return `${sortField.config.caption || sortField.meta.caption}${sortField.config.isGroupField ? " (Group)" : ""}`;
    }, [sortField, reportField]);

    useEffect(() => {
      if (!sortField.config.ordering) {
        if (onChangeSorting) onChangeSorting(sortField, Ordering.Ascending);
      }
    }, [sortField, sortField.config.ordering, onChangeSorting]);

    const [{ isDragging }, drag, dragPreview] = useDrag(
      () => ({
        type: AreaItemType.SORTS,
        item: sortField,
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
          if (monitor.didDrop()) {
            onEndMoveItem?.call(null, item);
          }
        },
        canDrag: () => !sortField.config.isGroupField && !readonlyMode,
      }),
      [sortField, readonlyMode]
    );

    const [, drop] = useDrop(
      () => ({
        accept: AreaItemType.SORTS,
        hover(item: SortField, monitor) {
          if (monitor.canDrop() && item.meta.name !== sortField.meta.name) {
            onDragMove?.call(null, item, sortField);
          }
        },
        canDrop: (_: SortField, monitor) => monitor.isOver() && !sortField.config.isGroupField,
      }),
      [sortField]
    );

    const handleRemoveItem = useCallback(() => {
      onRemoveItem?.call(null, sortField);
    }, [onRemoveItem, sortField]);

    const revertSorting = useCallback(() => {
      if (onChangeSorting)
        onChangeSorting(
          sortField,
          sortField.config.ordering === Ordering.Descending ? Ordering.Ascending : Ordering.Descending
        );
    }, [onChangeSorting, sortField]);

    const setRef = useCallback(
      (node: HTMLLIElement | null) => {
        drag(drop(node));
      },
      [drag, drop]
    );

    useEffect(() => {
      dragPreview(getEmptyImage());
    }, [dragPreview]);

    return (
      <ListItem
        role="Handle"
        ref={setRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: ".5rem",
          height: "32px",
          borderRadius: "2px",
          p: "5px 0",
          opacity: isDragging ? 0 : 1,
          backgroundColor: "background.grey",
          ...sx,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
            border: "1px solid",
            borderColor: sortField.config.isGroupField ? "action.disabled" : "text.disabled",
            borderRadius: "2px",
            backgroundColor: "background.paper",
            p: "0 5px",
            "&:hover": {
              cursor: readonlyMode ? "default" : "grab",
            },
            minHeight: "32px",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              flexGrow: 1,
              color: (theme) =>
                sortField.config.isGroupField || readonlyMode
                  ? theme.palette.text.disabled
                  : theme.palette.text.primary,
            }}
            noWrap
          >
            {caption}
          </Typography>
          {!sortField.config.isGroupField && !readonlyMode && (
            <CloseIconButton onClick={handleRemoveItem} iconProps={{ color: "disabled" }} />
          )}
        </Box>
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: makeTintedBackgroundFromColor(theme, theme.palette.primary.main),
            borderRadius: "2px",
            p: 0,
            "&:hover": {
              cursor: "pointer",
            },
          })}
          onClick={revertSorting}
        >
          <IconButton>
            {sortField.config.ordering === Ordering.Descending && <SortDescIcon color="primary" />}
            {sortField.config.ordering !== Ordering.Descending && <SortAscIcon color="primary" />}
          </IconButton>
        </Box>
      </ListItem>
    );
  }
);

SortFieldItem.displayName = "SortFieldItem";

export default SortFieldItem;
